import { z } from 'zod';
import { centimesPositifsSchema, idSchema } from '../model/commun.js';
import { typeOeuvreSchema } from '../model/artworks.js';
import { FORMATS_PROFIL, SEUILS_FORMAT_CM } from './format.js';
import { DEMI_VIE_JOURS, POIDS_SIGNAUX } from './score.js';
export const formatProfilSchema = z.enum(FORMATS_PROFIL);
export const seuilsFormatCmSchema = z
    .object({
    petit: z.number().positive(),
    moyen: z.number().positive(),
    grand: z.number().positive(),
})
    .refine((seuils) => seuils.petit < seuils.moyen && seuils.moyen < seuils.grand, {
    message: 'Les seuils doivent être strictement croissants.',
});
const textesProfilSchema = z
    .array(z.string().trim().min(1).max(80))
    .max(30)
    .transform((textes) => {
    const vus = new Set();
    return textes.filter((texte) => {
        const cle = texte.toLocaleLowerCase('fr');
        if (vus.has(cle))
            return false;
        vus.add(cle);
        return true;
    });
});
const budgetSchema = centimesPositifsSchema.nullable();
const budgetCoherent = (profil) => profil.budgetMinCentimes === null ||
    profil.budgetMaxCentimes === null ||
    profil.budgetMinCentimes <= profil.budgetMaxCentimes;
export const profilDeclareSchema = z
    .object({
    artistesRecherches: z.array(idSchema).default([]),
    techniques: textesProfilSchema.default([]),
    formats: z.array(formatProfilSchema).default([]),
    typesOeuvre: z.array(typeOeuvreSchema).default([]),
    budgetMinCentimes: budgetSchema.default(null),
    budgetMaxCentimes: budgetSchema.default(null),
    themes: z.array(z.string()).default([]),
    exclusions: z.array(idSchema).default([]),
    nePasSolliciter: z.boolean().default(false),
    notes: z.string().max(4000).default(''),
})
    .refine(budgetCoherent, {
    message: 'Le budget minimum dépasse le maximum.',
    path: ['budgetMaxCentimes'],
});
export const reglagesProfilsSchema = z.object({
    seuilsFormatCm: seuilsFormatCmSchema.default(SEUILS_FORMAT_CM),
    demiVieJours: z.int().min(7).max(730).default(DEMI_VIE_JOURS),
});
export const canalProfilSchema = z.enum(['email', 'telephone', 'rendez_vous', 'autre']);
export const prevenirProfilSchema = z.object({
    canal: canalProfilSchema,
    note: z.string().max(4000).default(''),
});
export const pasPourLuiProfilSchema = z.object({
    exclureArtiste: z.boolean().default(false),
    note: z.string().max(4000).default(''),
});
export const etatCorrespondanceSchema = z.enum(['a_prevenir', 'prevenu', 'pas_pour_lui']);
export const profilObserveSchema = z.object({
    artistes: z.array(z.object({
        artisteId: idSchema,
        nombre: z.int().positive(),
        formatDominant: formatProfilSchema.nullable(),
    })),
    techniques: z.array(z.object({ technique: z.string(), nombre: z.int().positive() })),
    formats: z.array(z.object({ format: formatProfilSchema, nombre: z.int().positive() })),
    typesOeuvre: z.array(z.object({ type: typeOeuvreSchema, nombre: z.int().positive() })),
    budgetMinCentimes: budgetSchema,
    budgetMaxCentimes: budgetSchema,
});
export const raisonCorrespondanceSchema = z.discriminatedUnion('code', [
    z.object({ code: z.literal('artiste_recherche'), params: z.object({ artisteId: idSchema }) }),
    z.object({
        code: z.literal('artiste_achete'),
        params: z.object({
            artisteId: idSchema,
            nombre: z.int().positive(),
            formatDominant: formatProfilSchema.nullable(),
        }),
    }),
    z.object({ code: z.literal('technique'), params: z.object({ technique: z.string() }) }),
    z.object({ code: z.literal('format'), params: z.object({ format: formatProfilSchema }) }),
    z.object({ code: z.literal('type'), params: z.object({ type: typeOeuvreSchema }) }),
    z.object({ code: z.literal('theme'), params: z.object({ theme: z.string() }) }),
    z.object({
        code: z.literal('budget'),
        params: z.object({ budgetMinCentimes: budgetSchema, budgetMaxCentimes: budgetSchema }),
    }),
    z.object({ code: z.literal('interet'), params: z.object({ score: z.int().min(0).max(100) }) }),
]);
export const reponseProfilSchema = z.object({
    declare: profilDeclareSchema,
    observe: profilObserveSchema,
    budgetMasque: z.boolean(),
    renseigneLe: z.iso.datetime().nullable(),
    renseignePar: idSchema.nullable(),
});
const scoreSchema = z.int().min(0).max(100);
const ligneScoreSchema = z.object({
    signal: z.enum(Object.keys(POIDS_SIGNAUX)),
    poids: z.number().nonnegative(),
    date: z.iso.datetime(),
    ageJours: z.number().nonnegative(),
    points: z.int().nonnegative(),
    objetType: z.string(),
    objetId: idSchema,
    libelle: z.string(),
});
export const reponseScoreProfilSchema = z.object({
    score: scoreSchema.nullable(),
    brut: z.number().nonnegative().nullable(),
    lignes: z.array(ligneScoreSchema),
    tendance: z.int().nullable(),
    calculeLe: z.iso.datetime().nullable(),
    exclu: z.boolean(),
    cause: z.enum(['anonymise', 'fusionne', 'archive', 'ne_pas_solliciter']).nullable(),
});
const classementCorrespondanceSchema = z.object({
    points: z.int().nonnegative(),
    raisons: z.array(raisonCorrespondanceSchema),
    scoreInteret: scoreSchema.nullable(),
});
export const correspondanceContactSchema = classementCorrespondanceSchema.extend({
    contactId: idSchema,
    nom: z.string(),
});
export const correspondanceOeuvreSchema = classementCorrespondanceSchema.extend({
    oeuvreId: idSchema,
    titre: z.string(),
});
export const reponseAPrevenirSchema = z.object({
    correspondances: z.array(correspondanceContactSchema),
    totalAPrevenir: z.int().nonnegative(),
    prevenus: z.int().nonnegative(),
    pasPourLui: z.int().nonnegative(),
    budgetMasque: z.boolean(),
});
export const reponseOeuvresProfilSchema = z.object({
    oeuvres: z.array(correspondanceOeuvreSchema),
    budgetMasque: z.boolean(),
});
export const reponseTableauProfilsSchema = z.object({
    oeuvres: z.array(z.object({
        oeuvreId: idSchema,
        titre: z.string(),
        nombre: z.int().positive(),
        meilleureCorrespondance: correspondanceContactSchema,
    })),
    budgetMasque: z.boolean(),
});
export const reponseScoresProfilsSchema = z.object({
    scores: z.array(z.object({ contactId: idSchema, score: scoreSchema.nullable() })),
});
export const reponseVocabulaireProfilsSchema = z.object({
    techniques: z.array(z.string()),
    themes: z.array(z.string()),
});
export const reponseReglagesProfilsSchema = reglagesProfilsSchema;
export const reponseGesteProfilSchema = z.object({
    etat: etatCorrespondanceSchema,
    traiteLe: z.iso.datetime(),
    traitePar: idSchema.nullable(),
    canal: canalProfilSchema.nullable(),
    note: z.string(),
    exclusionAjoutee: z.boolean(),
});
export const reponseRecalculCorrespondancesSchema = z.object({ enfile: z.boolean() });
//# sourceMappingURL=schemas.js.map